import React from 'react'
import { View } from 'react-native'
import { useTheme } from 'styled-components/native'
import moment from 'moment'

import I18n, { isTranslationKey } from 'APP/I18n'
import { ActiveMinutes } from '@dialogue/services'
import type { ActivityType, Challenge } from '@dialogue/services/dist/active-minutes/model'
import { nth } from 'APP/Lib/Helpers'

import { Images, Metrics, Colors } from 'APP/Themes'
import { CHALLENGE_ACTIVITY_ICONS } from '../constants'
import Typography from 'APP/Converse/Typography'
import { cleanupMetricValue } from 'APP/Lib/ActiveMinutes/challenges'
import { getLocalUnitLabelForMetric } from 'APP/Lib/ActiveMinutes/general/helpers'

import {
  Card,
  CardContainer,
  CardImage,
  CountdownIcon,
  DescriptionContainer,
  DetailLabel,
  DetailsContainer,
  DetailsItemContainer,
  RankIcon,
  TagContainer,
  TagCopy,
  TagListContainer,
  Title,
  TotalValueContainer,
} from './style'

export const CARD_SIZE = {
  SMALL: 'small',
  LARGE: 'large',
} as const

type CardSize = (typeof CARD_SIZE)[keyof typeof CARD_SIZE]

type LocalizedText = { en: string; fr: string }

type ChallengeCardChallenge = Pick<
  Challenge,
  'id' | 'status' | 'metric' | 'rank' | 'image' | 'title' | 'starts_at' | 'ends_at' | 'joined_at'
> & {
  activity?: ActivityType
  brought_by?: LocalizedText
  score?: number
}

type ActivityIcon = (typeof CHALLENGE_ACTIVITY_ICONS)[keyof typeof CHALLENGE_ACTIVITY_ICONS]

interface TagProps {
  icon: ActivityIcon
  copy?: string
}

const Tag: React.FC<TagProps> = ({ icon: TagIcon, copy }) => {
  const theme = useTheme()
  return (
    <TagContainer testID="tag">
      <TagIcon.Lib
        name={TagIcon.name}
        size={Metrics.icons.tiny}
        color={theme.colors.textInverted}
      />
      {copy && <TagCopy variant="caption">{copy}</TagCopy>}
    </TagContainer>
  )
}

interface ChallengeCardProps {
  size?: CardSize
  challenge: ChallengeCardChallenge
  onPress?: () => void
  analyticsName?: string
  testID?: string
}

const ChallengeCard: React.FC<ChallengeCardProps> = ({
  size = CARD_SIZE.LARGE,
  challenge,
  onPress,
  analyticsName = 'challenge-card',
  testID = 'CHALLENGE_CARD',
}) => {
  const isSmall = size === CARD_SIZE.SMALL
  const {
    id,
    status,
    activity,
    rank,
    image,
    title,
    brought_by,
    ends_at,
    starts_at,
    joined_at,
    metric,
    score,
  } = challenge
  const isCompleted = status === ActiveMinutes.Types.ChallengeStatus.Completed
  const shouldShowRank =
    status !== ActiveMinutes.Types.ChallengeStatus.Upcoming && !!joined_at && rank !== null

  const shouldShowTotalValue = isCompleted && !!joined_at && score !== undefined

  const activityKey = `WellnessCenter.challenges.activity.${activity}`
  const activityTag = activity && {
    icon: CHALLENGE_ACTIVITY_ICONS[activity as keyof typeof CHALLENGE_ACTIVITY_ICONS],
    copy: isTranslationKey(activityKey) ? I18n.t(activityKey) : undefined,
  }
  const subtitle = brought_by?.[I18n.baseLocale]

  const ongoingCountdown = `${I18n.t('WellnessCenter.challenges.countdown.ongoing')} ${moment(
    ends_at
  ).fromNow(true)}`
  const upcomingCountdown = `${I18n.t('WellnessCenter.challenges.countdown.upcoming')} ${moment(
    starts_at
  ).fromNow(true)}`
  const completedDateRange = `${moment(starts_at).format('MMM D')} - ${moment(ends_at).format(
    'MMM D'
  )}`
  const totalValue = score === undefined ? undefined : cleanupMetricValue(score, metric)
  const rankLabel =
    rank === null
      ? undefined
      : `${nth(rank, I18n.baseLocale)} ${I18n.t('WellnessCenter.challenges.rank')}`

  return (
    <CardContainer isSmall={isSmall}>
      <Card onPress={onPress} analyticsName={analyticsName} testID={testID} key={id}>
        <View>
          <CardImage
            // force use of cache on iOS
            source={{ uri: image?.[I18n.baseLocale], cache: 'force-cache' }}
            defaultSource={Images.contentLoading}
            resizeMode="cover"
          />
          <TagListContainer>
            {[activityTag].filter(Boolean).map((tagProps, index) => (
              <Tag key={index} {...(tagProps as TagProps)} />
            ))}
          </TagListContainer>
          <TagListContainer />
        </View>
        <DescriptionContainer>
          <Title variant="h4" numberOfLines={3}>
            {title?.[I18n.baseLocale]}
          </Title>
          {subtitle && (
            <Typography variant="bodySmall" testID="SUBTITLE">
              {subtitle}
            </Typography>
          )}
          <DetailsContainer testID="DETAILS">
            {/* Countdown */}
            <DetailsItemContainer>
              <CountdownIcon name="schedule" size={Metrics.icons.tiny} color={Colors.accent} />
              <DetailLabel variant="bodySmall" numberOfLines={1} testID="challengeCountdown">
                {status === ActiveMinutes.Types.ChallengeStatus.Ongoing && ongoingCountdown}
                {status === ActiveMinutes.Types.ChallengeStatus.Upcoming && upcomingCountdown}
                {status === ActiveMinutes.Types.ChallengeStatus.Completed && completedDateRange}
              </DetailLabel>
            </DetailsItemContainer>
            {/* Rank */}
            {shouldShowRank && (
              <DetailsItemContainer>
                <RankIcon name="trophy-outline" size={Metrics.icons.tiny} color={Colors.accent} />
                <DetailLabel variant="bodySmall" numberOfLines={1} testID="CHALLENGE_RANK">
                  {rankLabel}
                </DetailLabel>
              </DetailsItemContainer>
            )}
          </DetailsContainer>
          {shouldShowTotalValue && (
            <TotalValueContainer>
              <Typography variant="h4" testID="TOTAL_VALUE">
                {totalValue} {getLocalUnitLabelForMetric(metric)}
              </Typography>
            </TotalValueContainer>
          )}
        </DescriptionContainer>
      </Card>
    </CardContainer>
  )
}

export default ChallengeCard
